import React from 'react';
import { Warning, CheckCircle, Error } from '@mui/icons-material';

const BestPractices = () => {
    const practices = [
        {
            type: 'good',
            title: 'Utilisez HTTPS',
            description: 'Toujours utiliser HTTPS pour toutes les communications API',
            code: 'https://api.netikash.com'
        },
        {
            type: 'good',
            title: 'Variables d\'environnement',
            description: 'Stockez les clés secrètes dans des variables d\'environnement',
            code: 'process.env.CLIENT_SECRET'
        },
        {
            type: 'bad',
            title: 'Évitez le stockage local',
            description: 'Ne stockez jamais les clés dans le code source ou le localStorage',
            code: '// ❌ N\'écrivez pas ceci dans votre code\nconst secret = "ma-cle-secrete"'
        },
        {
            type: 'good',
            title: 'Validation des entrées',
            description: 'Validez toujours les données reçues de vos utilisateurs',
            code: 'if (isValidAmount(amount)) { /* traitement */ }'
        },
        {
            type: 'bad',
            title: 'Pas de logs sensibles',
            description: 'Ne logguez jamais les données de paiement ou tokens',
            code: '// ❌ console.log("Token:", accessToken)'
        },
        {
            type: 'good',
            title: 'Gestion des erreurs',
            description: 'Implémentez une gestion d\'erreurs sécurisée',
            code: 'try { /* opération */ } catch (error) { /* gestion sécurisée */ }'
        }
    ];

    const getPracticeIcon = (type) => {
        if (type === 'good') return CheckCircle;
        if (type === 'bad') return Error;
        return Warning;
    };

    const getPracticeColor = (type) => {
        if (type === 'good') return 'text-green-500';
        if (type === 'bad') return 'text-red-500';
        return 'text-amber-500';
    };

    const getPracticeBorder = (type) => {
        if (type === 'good') return 'border-green-200';
        if (type === 'bad') return 'border-red-200';
        return 'border-amber-200';
    };

    return (
        <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                        Bonnes Pratiques de Sécurité
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                        Suivez ces recommandations pour garantir la sécurité de votre intégration.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {practices.map((practice, index) => {
                        const IconComponent = getPracticeIcon(practice.type);
                        return (
                            <div key={index} className={`bg-white rounded-xl p-6 border-2 ${getPracticeBorder(practice.type)} hover:shadow-md transition-shadow`}>
                                <div className="flex items-start space-x-3 mb-4">
                                    <IconComponent className={`text-xl mt-1 ${getPracticeColor(practice.type)}`} />
                                    <div>
                                        <h3 className="font-semibold text-gray-900">{practice.title}</h3>
                                        <p className="text-sm text-gray-600 mt-1">{practice.description}</p>
                                    </div>
                                </div>

                                <div className="bg-gray-900 rounded-lg p-3">
                                    <code className="text-green-400 text-sm font-mono">
                                        {practice.code}
                                    </code>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* SecurityPage Checklist */}
                <div className="mt-12 bg-white rounded-2xl p-8 shadow-sm">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                        Checklist de Sécurité
                    </h3>
                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-4">✅ À Faire</h4>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex items-center space-x-3">
                                    <CheckCircle className="text-green-500" />
                                    <span>Utiliser la dernière version du SDK</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <CheckCircle className="text-green-500" />
                                    <span>Implémenter la validation côté serveur</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <CheckCircle className="text-green-500" />
                                    <span>Mettre à jour régulièrement les dépendances</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <CheckCircle className="text-green-500" />
                                    <span>Auditer votre code régulièrement</span>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-4">❌ À Éviter</h4>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex items-center space-x-3">
                                    <Error className="text-red-500" />
                                    <span>Utiliser HTTP au lieu de HTTPS</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <Error className="text-red-500" />
                                    <span>Exposer les clés dans le code client</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <Error className="text-red-500" />
                                    <span>Ignorer les erreurs SSL/TLS</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BestPractices;